import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { UploadedFile } from 'interfaces/file';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { ApiErrorAlert } from 'components/ui/api-error-alert';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { useRequireAuth } from 'hooks/useRequireAuth';
import { useService } from 'hooks/useService';
import { useToast } from 'hooks/useToast';
import { ApiError } from 'interfaces/api';
import { Column } from 'interfaces/column';
import { DatasetStub } from 'interfaces/dataset';
import { ContentType } from 'services/abstract';
import { CsvUploader } from './CsvUploader';
import DatasetColumnPicker from './DatasetColumnPicker';

type FormData = DatasetStub & {
  fragmentName: string;
  columns: Column[];
};

type PageCartoDataFormProps = {
  pageCartoId: number;
  onSubmit: (dataForm: FormData) => void;
};

export const PageCartoDataForm: FC<PageCartoDataFormProps> = ({
  pageCartoId,
  onSubmit,
}) => {
  const queryClient = useQueryClient();
  const { user } = useRequireAuth();
  const [columns, setColumns] = useState<Column[]>([]);
  const { addToast } = useToast();
  const datasetService = useService(ContentType.DATASET);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    control,
    watch,
  } = useForm<FormData>({
    defaultValues: { isPublic: false },
  });

  const { isError, error, isLoading, mutateAsync } = useMutation({
    mutationFn: async ({ columns, fragmentName, ...dataset }: FormData) => {
      return await datasetService.addDataToPageCarto(
        pageCartoId,
        fragmentName,
        { ...dataset, owner: user?.id || 0 },
        columns
      );
    },
    onSuccess: (_response, formData) => {
      addToast({
        title: 'Jeu de données importé',
        description: `Votre jeu de données a été crée avec succès`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['page-carto', pageCartoId] });
      queryClient.invalidateQueries({
        queryKey: ['page-carto-dataset', pageCartoId],
      });
      onSubmit(formData);
    },
  });

  const handleFileUpload = (file: UploadedFile) => {
    setValue('name', file?.name || '');
    setValue('fragmentName', file?.name || '');
  };

  const handleFileCancel = () => {
    setValue('name', '');
    setValue('fragmentName', '');
    setColumns([]);
  };
  const handleFileParse = (columns: Column[]) => {
    setColumns(columns);
  };

  const onSubmitForm = (data: FormData) => {
    mutateAsync(data);
  };

  const isPublic = watch('isPublic', false);

  return (
    <div className="p-4 bg-slate-100 border border-slate-300 rounded shadow-xl">
      <form onSubmit={handleSubmit(onSubmitForm)}>
        {isError && <ApiErrorAlert error={error as ApiError} />}
        <div className="flex flex-row">
          <div className="flex flex-col w-1/2 p-2">
            <div className="border-2 text-xs italic text-red-500 p-3">
              <p>
                * Il est possible de renseigner la source et la validité des
                données pour chaque cellule de chaque colonne du fichier CSV en
                utilisant la syntaxe suivante : COLONNE X [s=valeur&v=valeur]
              </p>
            </div>
            <div className="mt-3">
              <Label htmlFor="name">
                1. Uploader des données à partir d&apos;un fichier CSV
              </Label>
              <Controller
                name="csv"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: 'Le fichier CSV est obligatoire',
                }}
                render={({ field }) => {
                  return (
                    <CsvUploader
                      onUpload={handleFileUpload}
                      onParse={handleFileParse}
                      onCancel={handleFileCancel}
                      {...field}
                    />
                  );
                }}
              />
              {errors.csv && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.csv.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label>2. Sélectionnez les colonnes à utiliser :</Label>
              <Controller
                name="columns"
                control={control}
                defaultValue={undefined}
                rules={{
                  required:
                    'Vous devez sélectionner les colonnes ainsi que le Géo Code.',
                  validate: (cols) => {
                    return cols.length > 0 && cols.some((c) => c.isGeoCode);
                  },
                }}
                render={({ field }) => {
                  return <DatasetColumnPicker data={columns} {...field} />;
                }}
              />
            </div>
          </div>
          <div className="flex flex-col w-1/2 p-2">
            <div className="mt-3">
              <Label htmlFor="name">Nom du jeu de données</Label>
              <Input
                id="fragmentName"
                className="w-full"
                {...register('fragmentName', {
                  required: `Veuillez saisir le nom de votre jeu de données`,
                })}
              />
              {errors.fragmentName && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.fragmentName.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label htmlFor="origin">Origine du fichier</Label>
              <Input
                id="origin"
                data-testid="origin-input"
                className="w-full"
                {...register('origin', {
                  required: `Veuillez saisir l'origine de votre fichier de données`,
                })}
              />
              {errors.origin && (
                <div className="mt-2 text-xs text-red-600">
                  {errors.origin.message}
                </div>
              )}
            </div>
            <div className="mt-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is-public-toggle"
                  checked={isPublic}
                  onCheckedChange={(checked) => {
                    setValue('isPublic', checked, { shouldDirty: true });
                  }}
                />
                <Label htmlFor="is-public-toggle" className="cursor-pointer">
                  Accès Public
                </Label>
              </div>
              <Alert className="p-2 text-xs" variant="warning">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <AlertTitle>Attention : risque de non conformité</AlertTitle>
                <AlertDescription>
                  <span className="text-xs">
                    (Origine du fichier, source et validité des colonnes)
                  </span>
                </AlertDescription>
              </Alert>
            </div>
            <div className="flex justify-end mt-3">
              <Button
                type="submit"
                size="lg"
                data-testid="validateform-button"
                isLoading={isLoading}
                disabled={!isValid}
              >
                Valider
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
